/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from "react";
import { Card, Grid, Stack, Step, StepLabel, Stepper } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import Loader from "utils/Loader";
import { postAPICall } from "lib/callApi";
import { enqueueSnackbar } from "notistack";
import { UPLOAD_BULK_IMPORT_CSV, VALIDATE_BULK_IMPORT, USERS_BULK_IMPORT } from "lib/constants";
import { useNavigate } from "react-router-dom";

import "../../../styles/compete.scss";
import ImportList from "./Steps/ImportList";
import Validate from "./Steps/Validate";
import Summary from "./Steps/Summary";

function getSteps() {
  return ["Import List", "Validate", "Summary"];
}

function UsersBulkUpload() {
  const formRef = useRef();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [validatingFile, setValidatingFile] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState();

  const [isValidBulkUploadData, setIsValidBulkUploadData] = useState(false);
  const [bulkUploadUsersData, setBulkUploadUsersData] = useState([]);
  const [bulkUploadSummaryData, setBulkUploadSummaryData] = useState({});
  const [subscriptionData, setSubscriptionData] = useState({});

  const [shouldRevalidate, setShouldRevalidate] = useState(false);
  const steps = getSteps();

  const isLastStep = activeStep === steps.length - 1;

  const handleBack = () => setActiveStep(activeStep - 1);

  const handleNext = async () => {
    if (shouldRevalidate) {
      return formRef.current.triggerSubmit("revalidate");
    }

    if (isLastStep) {
      return formRef.current.triggerSubmit();
    }

    setActiveStep(activeStep + 1);
    return true;
  };

  const uploadBulkImportFile = async () => {
    if (selectedFile) {
      setValidatingFile(true);
      const formData = new FormData();
      formData.append("csvFile", selectedFile);
      const result = await postAPICall(UPLOAD_BULK_IMPORT_CSV, true, formData, {
        "content-type": "multipart/form-data",
      });
      if (result?.data?.success) {
        const { formattedUsers, isValid, subscription, ...rest } = result.data.data;
        setIsValidBulkUploadData(isValid || false);
        setBulkUploadUsersData(formattedUsers || []);
        setBulkUploadSummaryData(rest || {});
        setSubscriptionData(subscription || {});
      } else {
        setSelectedFile(null);
        setIsValidBulkUploadData(false);
        setBulkUploadUsersData([]);
        setBulkUploadSummaryData({});
        setSubscriptionData({});
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              result?.response?.data?.message || result?.data?.message || "Something went wrong!",
          }),
          {
            variant: "custom",
          }
        );
      }

      setValidatingFile(false);
    } else {
      setIsValidBulkUploadData(false);
      setBulkUploadUsersData([]);
      setBulkUploadSummaryData({});
      setSubscriptionData({});
    }
  };

  const reValidateBulkUpload = async (data) => {
    try {
      setValidatingFile(true);
      const response = await postAPICall(VALIDATE_BULK_IMPORT, true, data);
      if (response?.data?.success) {
        const { formattedUsers, isValid, subscription, ...rest } = response.data.data;
        setIsValidBulkUploadData(isValid || false);
        setBulkUploadUsersData(formattedUsers || []);
        setBulkUploadSummaryData(rest || {});
        setSubscriptionData(subscription || {});
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "error",
            text:
              response?.data?.message ||
              "Something went wrong while validating bulk upload data, Please try again!",
          }),
          {
            variant: "custom",
          }
        );
      }
      setValidatingFile(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "error",
          text: "Something went wrong while validating bulk upload data, Please try again!",
        }),
        {
          variant: "custom",
        }
      );
      setValidatingFile(false);
    }
  };

  const handleBulkUserSubmit = async (data) => {
    setSubmitLoading(true);
    try {
      const response = await postAPICall(USERS_BULK_IMPORT, true, data);
      if (response?.data?.success) {
        navigate("/settings?selectedTab=teams");
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "error",
            text:
              response?.data?.message ||
              "Something went wrong while validating bulk upload data, Please try again!",
          }),
          {
            variant: "custom",
          }
        );
      }
      setSubmitLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "error",
          text: "Something went wrong while validating bulk upload data, Please try again!",
        }),
        {
          variant: "custom",
        }
      );
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    uploadBulkImportFile();
  }, [selectedFile]);

  return (
    <DashboardLayout>
      {/* header */}
      <DashboardNavbar />
      <MDBox py={3} className="competition-form-wrapper">
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={12} lg={10}>
            <MDTypography variant="h4">Users Bulk Upload</MDTypography>
          </Grid>
        </Grid>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", mt: 8 }}>
          <Grid item xs={12} lg={10}>
            <Card sx={{ height: "100%" }}>
              <MDBox mx={4} mt={-3}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>
              <MDBox sx={{ position: "relative" }} p={3}>
                <Loader loading={isLoading || submitLoading || validatingFile} />

                <MDBox>
                  <MDBox display={activeStep === 0 ? "block" : "none"}>
                    <ImportList
                      setIsLoading={setIsLoading}
                      selectedFile={selectedFile}
                      setSelectedFile={setSelectedFile}
                    />
                  </MDBox>
                  <MDBox display={activeStep === 1 ? "block" : "none"}>
                    <Validate
                      ref={formRef}
                      setShouldRevalidate={setShouldRevalidate}
                      isValidBulkUploadData={isValidBulkUploadData}
                      bulkUploadUsersData={bulkUploadUsersData}
                      reValidateBulkUpload={reValidateBulkUpload}
                      handleBulkUserSubmit={handleBulkUserSubmit}
                      subscriptionData={subscriptionData}
                    />
                  </MDBox>
                  <MDBox display={activeStep === 2 ? "block" : "none"}>
                    <Summary
                      bulkUploadSummaryData={bulkUploadSummaryData}
                      subscriptionData={subscriptionData}
                    />
                  </MDBox>
                  <MDBox
                    mt={isLastStep ? 2 : 4}
                    width="100%"
                    px={1}
                    display="flex"
                    justifyContent="space-between"
                  >
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="gradient" color="secondary" onClick={handleBack}>
                        PREV
                      </MDButton>
                    )}
                    <Stack direction="row" spacing={2}>
                      <MDButton
                        type="submit"
                        onClick={handleNext}
                        disabled={
                          submitLoading ||
                          !selectedFile ||
                          validatingFile ||
                          (!shouldRevalidate &&
                            activeStep === 1 &&
                            (!isValidBulkUploadData || subscriptionData?.subscriptionError))
                        }
                        color={
                          shouldRevalidate ? "error" : isLastStep ? "secondaryGreen" : "gray80"
                        }
                      >
                        {shouldRevalidate
                          ? "REVALIDATE"
                          : isLastStep
                          ? submitLoading
                            ? "Loading"
                            : "IMPORT"
                          : validatingFile
                          ? "Validating file..."
                          : "NEXT"}
                      </MDButton>
                    </Stack>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default UsersBulkUpload;
